<script lang="ts">
  import OnboardingWizardShell from "$lib/components/onboarding/OnboardingWizardShell.svelte";
  import { dataSources, ssoProviders } from "$lib/utils/constants";
  import { push, querystring } from "svelte-spa-router";

  import { selectedOrgStore } from "$stores/orgs";
  import { makeApiRequest } from "$lib/utils/api";
  import ConnectionConfigurationReview from "$lib/ui/ConnectionConfigurationReviewView/ConnectionConfigurationReview.svelte";

  import InviteAdminForm from "$lib/components/integrations/InviteAdminFlyoverV2.svelte";
  import { derived } from "svelte/store";
  import { sidebar } from "$lib/stores/sidebar.svelte";
  import { onDestroy } from "svelte";
  import SuccessfulInviteFlyover from "$lib/components/integrations/SuccessfulInviteFlyover.svelte";

  let selectedProvider = $state(ssoProviders.entra);
  let emailLength = $state(0);

  async function removeAdminInvite(admin: string, provider: string) {
    let body: {
      sso?: {
        provider: string;
        admins: {
          [email: string]: boolean;
        };
      };
      data_sources?: {
        provider: string;
        admins: {
          [email: string]: boolean;
        };
      }[];
    } = {};

    if ($selectedOrgStore?.sso) {
      body["sso"] = {
        provider: $selectedOrgStore.sso.provider,
        admins: Object.fromEntries(
          $selectedOrgStore.sso.invitedAdmins
            .filter((email) => !(email === admin && $selectedOrgStore.sso?.provider === provider))
            .map((email) => [email, false]),
        ),
      };
    }

    body["data_sources"] = $selectedOrgStore?.dataSources.map((ds) => ({
      provider: ds.provider,
      admins: Object.fromEntries(
        ds.invitedAdmins
          .filter((email) => !(email === admin && ds.provider === provider))
          .map((email) => [email, false]),
      ),
    }));

    await makeApiRequest(`${$selectedOrgStore!.resourcePrefix}/`, body);
  }

  async function resendAdminInvite(admin: string, provider: string) {
    let body: {
      sso?: {
        provider: string;
        admins: {
          [email: string]: boolean;
        };
      };
      data_sources?: {
        provider: string;
        admins: {
          [email: string]: boolean;
        };
      }[];
    } = {};

    if ($selectedOrgStore?.sso) {
      body["sso"] = {
        provider: $selectedOrgStore.sso.provider,
        admins: Object.fromEntries(
          $selectedOrgStore.sso.invitedAdmins.map((email) => [
            email,
            admin === email && $selectedOrgStore.sso?.provider === provider,
          ]),
        ),
      };
    }

    body["data_sources"] = $selectedOrgStore?.dataSources.map((ds) => ({
      provider: ds.provider,
      admins: Object.fromEntries(ds.invitedAdmins.map((email) => [email, admin === email && ds.provider === provider])),
    }));

    await makeApiRequest(`${$selectedOrgStore!.resourcePrefix}/`, body);
  }

  async function onAddAdmin(provider: string) {
    push(`${$selectedOrgStore?.pagePrefix}/review?action=invite&provider=${provider}`);
  }

  $inspect($selectedOrgStore);

  const unsubFromQueryString = derived([querystring, selectedOrgStore], (b) => b).subscribe(async ([qs, org]) => {
    const sp = new URLSearchParams(qs);
    if (sp.has("provider")) {
      const paramProvider = sp.get("provider")!;
      if (ssoProviders.hasOwnProperty(paramProvider) || dataSources.hasOwnProperty(paramProvider)) {
        selectedProvider = (ssoProviders[paramProvider] ?? dataSources[paramProvider])!;
      }
    }

    switch (sp.get("action")) {
      case "invite":
        sidebar.snippet = InviteAdmin;
        break;
      case "inviteSuccess":
        sidebar.snippet = InviteSuccess;
        break;
      default:
        sidebar.snippet = null;
        break;
    }
  });

  async function handleEmailSubmit({ emails }: { emails: string[] }) {
    if (!selectedProvider) {
      console.error("No selected SSO provider");
      return;
    }
    emailLength = emails.length;
    let otherAdmins = {};
    let body: any = {};

    if (
      Object.hasOwn(ssoProviders, selectedProvider.id) &&
      selectedProvider.name === $selectedOrgStore?.sso?.provider
    ) {
      otherAdmins = Object.fromEntries($selectedOrgStore?.sso.invitedAdmins.map((email) => [email, false]));
      body["sso"] = {
        provider: selectedProvider.name,
        admins: { ...otherAdmins, ...Object.fromEntries(emails.map((email) => [email, true])) },
      };
    } else if (Object.hasOwn(dataSources, selectedProvider.id)) {
      otherAdmins = Object.fromEntries([
        ...(
          $selectedOrgStore?.dataSources.find((ds) => ds.provider === selectedProvider.name)?.invitedAdmins ?? []
        ).map((email) => [email, false]),
        ...emails.map((email) => [email, true]),
      ]);

      body["data_sources"] = [
        {
          provider: selectedProvider.name,
          admins: otherAdmins,
        },
      ];
    }

    try {
      await makeApiRequest(`${$selectedOrgStore?.resourcePrefix}/`, body);
      // TODO: hard-coded entra
      push(`${$selectedOrgStore?.pagePrefix}/review?action=inviteSuccess&provider=${selectedProvider.id}`);
    } catch (error) {
      console.error("Error sending invites:", error);
    }
  }

  onDestroy(() => {
    unsubFromQueryString();
  });
</script>

{#snippet InviteAdmin()}
  <InviteAdminForm connectionId={selectedProvider.name} onSubmit={handleEmailSubmit} />
{/snippet}

{#snippet InviteSuccess()}
  <SuccessfulInviteFlyover connectionId={selectedProvider.name} emailsLength={emailLength} />
{/snippet}

<OnboardingWizardShell
  currentStep={2}
  onBack={() => push(`${$selectedOrgStore?.pagePrefix}/connect_data`)}
  onFinish={() => push(`${$selectedOrgStore?.pagePrefix}/`)}
>
  <h2 class="text-3xl font-semibold text-offblack mb-3">Review</h2>
  <p class="font-normal text-offblack">
    Review and confirm the SSO providers and data sources that you have connected to. Further changes can be made later.
  </p>
  <div class="relative inset-0 flex items-center my-3" aria-hidden="true">
    <div class="w-full border-t border-gray-300"></div>
  </div>
  <div class="flex flex-col gap-6 max-w-5xl mx-auto">
    <div class="flex justify-between w-full">
      <span class="font-semibold">SSO Provider</span>
      {#if $selectedOrgStore?.sso}
        <!-- TODO: hard-coded to entra - need to fix provider id -->
        {@const provider = ssoProviders.entra}
        {#if provider}
          <ConnectionConfigurationReview
            invitedAdmins={$selectedOrgStore.sso.invitedAdmins}
            {provider}
            {removeAdminInvite}
            {resendAdminInvite}
            connected_by={$selectedOrgStore.sso.connected_by}
            {onAddAdmin}
          ></ConnectionConfigurationReview>
        {/if}
      {:else}
        <div>Connect an SSO provider to continue</div>
      {/if}
    </div>

    <div class="relative inset-0 flex items-center my-3" aria-hidden="true">
      <div class="w-full border-t border-gray-300"></div>
    </div>

    <div class="flex justify-between w-full">
      <span class="font-semibold">Data Sources</span>
      {#if $selectedOrgStore}
        {#each $selectedOrgStore.dataSources as dataSource}
          <!-- TODO: hard-coded to entra - need to fix provider id -->
          {@const provider = dataSources.sharepoint}
          <ConnectionConfigurationReview
            invitedAdmins={dataSource.invitedAdmins}
            {provider}
            {removeAdminInvite}
            {resendAdminInvite}
            connected_by={dataSource.connected_by}
            {onAddAdmin}
          ></ConnectionConfigurationReview>
        {/each}
      {/if}
    </div>
  </div>
</OnboardingWizardShell>
